import { useState } from "react";
import { getPricebookPeriods } from "../../../../services";
import { PricebookPeriod } from "../../../../types/price-book.type";
import { PeriodDialogTarget } from "./PeriodDialog";

/** The API's page-size cap; one line never carries anywhere near this many periods. */
const LINE_PERIOD_LIMIT = 100;
const PERIOD_GONE = "ช่วงราคานี้สิ้นสุดหรือถูกลบแล้ว";

/**
 * Which period the book's PeriodDialog is open on. The สินค้า tab opens it from a row (a new
 * period of that line, or the running one from its chip) and the ช่วงราคา tab from its own
 * rows, so one dialog serves both. `onPeriodGone` refreshes the lines when a chip turns out
 * to show a period that has since ended or been deleted.
 */
export function usePeriodDialog(pricebookUuid: string, onPeriodGone: () => void) {
  const [target, setTarget] = useState<PeriodDialogTarget | null>(null);
  const [goneMessage, setGoneMessage] = useState<string>("");

  /** A new period; with no product code the dialog asks for one. */
  const openCreate = (productCode: string | null) =>
    setTarget({ kind: "create", productCode });

  const openEdit = (period: PricebookPeriod) =>
    setTarget({ kind: "edit", period });

  /**
   * The row chip knows only the running period's id and dates; editing needs the whole period
   * (its note and own prices), so the line's periods are read first.
   */
  const openEditById = async (productCode: string, periodUuid: string) => {
    setGoneMessage("");
    try {
      const {
        data: { data },
      } = await getPricebookPeriods(pricebookUuid, 1, LINE_PERIOD_LIMIT, {
        product_code: productCode,
      });
      const period = data?.rows.find((row) => row.uuid === periodUuid);
      if (period) {
        setTarget({ kind: "edit", period });
        return;
      }
      setGoneMessage(PERIOD_GONE);
      onPeriodGone();
    } catch {
      // The axios interceptor shows the failure; the dialog simply stays closed.
    }
  };

  const close = () => setTarget(null);

  const dismissGoneMessage = () => setGoneMessage("");

  return {
    target,
    goneMessage,
    openCreate,
    openEdit,
    openEditById,
    close,
    dismissGoneMessage,
  };
}
